import type {DraftGuessModelV1, GuessModelV1, QuestionModelV3} from '@albert-io/models';
import {List} from 'immutable';
import type {SetState, GetState, OrderingItem} from './types';

/**
 * Ordering question data model:
 * - items [{id, value}]: array order = initial (shuffled) display for students
 * - slots [{id}]: array order = correct answer order (positions)
 * - validResponse {slotId → itemId}: maps each correct-position slot to its item
 *
 * Guess content maps slots[i] → items[i] (simple positional mapping).
 * A correct guess has guessContent[slots[i].id] === validResponse[slots[i].id] for all i.
 */

/** Normalize raw Immutable item attributes into plain OrderingItem objects. */
const parseItems = (itemsAttribute: any): OrderingItem[] =>
  itemsAttribute.toJS().map((item: {id?: string; value?: string | string[]}, index: number) => ({
    id: item.id || `word-${index}`,
    value: Array.isArray(item.value) ? `[${item.value}]` : item.value || ''
  }));

/** Build an id→item lookup from an array of OrderingItems. */
const buildItemById = (allItems: OrderingItem[]): Record<string, OrderingItem> => {
  const map: Record<string, OrderingItem> = {};
  allItems.forEach((item) => {
    map[item.id] = item;
  });
  return map;
};

const actions = (set: SetState, get: GetState) => ({
  addQuestionInfo: (question: QuestionModelV3, useInitialOrder = false) => {
    const {guess, isSubmitted, items} = get();

    // If we're in "Show Solution" mode (isSubmitted=true but no real guess),
    // only update the question reference — don't reinitialize items/correctAnswer
    if (isSubmitted && !guess) {
      set((state) => {
        state.question = question;
      });
      return;
    }

    const attributes = question.getQuestionTypeAttributes().get('ordering');
    const slotsJs = attributes.get('slots', List()).toJS();
    const allItems = parseItems(attributes.get('items'));
    const itemById = buildItemById(allItems);
    let correctAnswer: Record<string, string> | null = null;
    let guessContent: Record<string, string> = {};
    let updatedIsSubmittedVal = isSubmitted;

    try {
      correctAnswer = question.getValidResponse().toJS();
    } catch (_err) {
      correctAnswer = null;
    }

    if (isSubmitted && guess && guess.getContent) {
      try {
        guessContent = guess.getContent().toJS();
      } catch (_err) {
        updatedIsSubmittedVal = false;
      }
    }

    // Items are already in initial (shuffled) order — use directly
    const questionItems = allItems;

    // Reconstruct submitted answer: for each position i, slots[i] tells us the position,
    // guessContent[slots[i].id] tells us which item the student placed there
    const submittedAnswer = slotsJs.map((slot: {id: string}, index: number) => {
      const itemId = guessContent[slot.id] || `empty-${index}`;
      return {
        id: itemId,
        value: itemById[itemId]?.value ?? ''
      };
    });

    let updatedItems;
    if (isSubmitted) {
      updatedItems = items.length > 0 ? items : submittedAnswer;
    } else {
      updatedItems = useInitialOrder || items.length === 0 ? questionItems : items;
    }

    set((state) => {
      state.question = question;
      state.correctAnswer = correctAnswer;
      state.items = updatedItems;
      state.activeItemId = null;
      state.guess = updatedIsSubmittedVal ? guess : null;
      state.isSubmitted = updatedIsSubmittedVal;
    });
  },

  addDraftGuessInfo: (draftGuess: DraftGuessModelV1 | null) => {
    const {question} = get();
    const hasQuestionInfo = question !== null && question.getQuestionTypeAttributes !== undefined;
    if (!hasQuestionInfo || !draftGuess) return;
    if (draftGuess?.getContent?.()) {
      const questionItems = question
        .getQuestionTypeAttributes()
        .getIn(['ordering', 'items'], List())
        .toJS();

      const ItemInfo = questionItems.reduce(
        (acc: {[x: string]: any}, item: {id: string | number}) => {
          acc[item.id] = item;
          return acc;
        },
        {}
      );

      const questionSlots = question
        .getQuestionTypeAttributes()
        .getIn(['ordering', 'slots'], List())
        .toJS();

      set((state) => {
        const draftContentObject: {[slotId: string]: string} = draftGuess.getContent().toJS();
        // Reconstruct display order: for each position i, slots[i] tells us the position,
        // draftContent[slots[i].id] tells us which item the student had there
        const orderedItemList = questionSlots
          .map((slot: {id: string}) => {
            const itemId = draftContentObject[slot.id];
            const itemInfo = ItemInfo[itemId];
            return itemId
              ? {
                  id: itemId,
                  value: itemInfo ? itemInfo.value : ''
                }
              : null;
          })
          .filter((item: any) => item != null);

        state.items = orderedItemList;
        state.draftGuess = draftGuess;
      });
    }
  },

  addGuessInfo: (guess: GuessModelV1 | null) => {
    set((state) => {
      state.guess = guess;
      state.isSubmitted = !!guess;
    });
  },

  setIsDragging: (isDragging: boolean) => {
    set((state) => {
      state.isDragging = isDragging;
    });
  },

  buildGuessContent: () => {
    const {question, items} = get();
    if (!question) return {};

    const slots =
      question.getQuestionTypeAttributes()?.getIn(['ordering', 'slots'], List()).toJS() || [];
    const content: Record<string, string> = {};

    // Simple positional mapping: slots[i] → items[i]
    slots.forEach((slot: {id: string}, index: number) => {
      const itemId = items[index]?.id;
      if (slot.id && itemId) {
        content[slot.id] = itemId;
      }
    });
    return content;
  },

  // interaction actions
  setActiveItem: (itemId: string | null) => {
    set((state) => {
      state.activeItemId = itemId;
    });
  },
  setHoveredDividerId: (dividerId: string | null) => {
    set((state) => {
      state.hoveredDividerId = dividerId;
    });
  },
  setDraggedItemId: (itemId: string | null) => {
    set((state) => {
      state.draggedItemId = itemId;
    });
  },
  moveItemToPosition: (itemId: string, targetIndex: number) => {
    const currentItems = get().items;
    const itemIndex = currentItems.findIndex((item) => item.id === itemId);
    if (itemIndex === -1) return;

    const newItems = [...currentItems];
    const [movedItem] = newItems.splice(itemIndex, 1);

    // Adjust target index if we're moving from before the target
    const adjustedIndex = itemIndex < targetIndex ? targetIndex - 1 : targetIndex;

    newItems.splice(adjustedIndex, 0, movedItem);

    set((state) => {
      state.items = newItems;
    });
  },

  setPreviewTargetIndex: (index: number | null) => {
    set((state) => {
      state.previewTargetIndex = index;
    });
  },

  clearGuess: () => {
    const {question} = get();
    let questionItems: OrderingItem[] = [];

    if (question) {
      try {
        const attributes = question.getQuestionTypeAttributes().get('ordering');
        questionItems = parseItems(attributes.get('items'));
      } catch (_err) {
        questionItems = [];
      }
    }

    set((state) => {
      state.guess = null;
      state.isSubmitted = false;
      state.correctAnswer = null;
      state.items = questionItems;
      state.activeItemId = null;
    });
    setTimeout(() => {
      global.scrollTo(0, 0);
    }, 0);
  },

  showSolution: () => {
    const {question} = get();
    if (!question) return;

    let correctAnswer: Record<string, string> | null = null;
    try {
      correctAnswer = question.getValidResponse().toJS();
    } catch (_err) {
      return;
    }

    // Correct order is defined by slots: for each slot position i,
    // validResponse[slots[i].id] gives the correct item
    const attributes = question.getQuestionTypeAttributes().get('ordering');
    const allItems = parseItems(attributes.get('items'));
    const itemById = buildItemById(allItems);
    const slots = attributes.get('slots', List()).toJS();

    const correctOrderItems = slots.map((slot: {id: string}, index: number) => {
      const itemId = correctAnswer?.[slot.id];
      return itemById[itemId ?? ''] || allItems[index] || {id: slot.id, value: ''};
    });

    set((state) => {
      state.isSubmitted = true;
      state.correctAnswer = correctAnswer;
      state.items = correctOrderItems;
      state.guess = null;
    });
  }
});

export default actions;
